import React, {useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {IReduxRootState} from '../../@interfaces';
import {BootstrapColors} from '../../@types/Bootstrap';
import Popover from '../Popover/Popover';
import CircularProgressBar from '../ProgressBar/Circular/CircularProgressBar';
import classnames from '../../helpers/classnames';

type Props = { offsetTop?: number } & Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'style'>;

const styles: React.CSSProperties = {
    borderRadius: '50%',
    width: '10px',
    height: '10px',
    display: 'inline-block',
    marginTop: '1px',
    cursor: 'pointer',
};

export default function WebSocketConnectionIndicator({offsetTop, className, ...rest}: Props) {
    const ref = useRef<HTMLSpanElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const {connectionState, errorMessage} = useSelector((state: IReduxRootState) => state.services.websocket);

    const indicatorColors: Record<typeof connectionState, BootstrapColors> = {
        initialized: 'info',
        connecting: 'warning',
        connected: 'success',
        unavailable: 'danger',
        disconnected: 'danger',
        failed: 'danger',
    };

    return <div className={classnames(className, 'd-inline-block')}>
        <CircularProgressBar
                size={22}
                progress={connectionState === 'connecting' ? undefined : 0}
                color={indicatorColors[connectionState]}
                transparentTrack
                shouldSpin={connectionState === 'connecting'}
        >
            <span
                    ref={ref}
                    style={offsetTop == null ? styles : {...styles, marginTop: `${offsetTop}px`}}
                    className={`bg-${indicatorColors[connectionState]}`}
                    {...rest}
                    onMouseEnter={() => setIsOpen(true)}
                    onMouseLeave={() => setIsOpen(false)}
            />
        </CircularProgressBar>

        <Popover
                isOpen={isOpen}
                itemRef={ref}
                placement="top"
                offset={[0, 15]}
        >
            <div className="d-inline-block me-2">WebSocket state:</div>
            <div className={classnames('py-1 px-2 rounded-2 d-inline-block', [
                `bg-${indicatorColors[connectionState]}`,
                `text-bg-${indicatorColors[connectionState]}`
            ])}>
                {connectionState}
            </div>
            {errorMessage ? <div className="alert alert-danger py-1 px-2 m-0 mt-3">{errorMessage}</div> : null}
        </Popover>
    </div>;
}
